import React, { useMemo } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import { useCreateNoteTemplateMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate } from "react-router-dom";

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [newNoteTemplate, { isLoading }] = useCreateNoteTemplateMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const navigate = useNavigate();

  const stripHtmlTags = (html: string) => {
    return html.replace(/<\/?[^>]+(>|$)/g, "");
  };

  const formik = useFormik({
    initialValues: {
      templateName: "",
      subject: "",
      message: "",
    },
      validate: (values) => {
      const errors: { [key: string]: string } = {};

       if (!values.templateName) errors.templateName = "Template Name is required";
       if (!values.subject) errors.subject = "Subject is required";
       if (!stripHtmlTags(values.message).trim()) errors.message = "Message is required";

       return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          note_title: values.templateName,
          subject: values.subject,
          note_message: stripHtmlTags(values.message),
        };

        const addTemplate = await newNoteTemplate(temData).unwrap();
        if (!addTemplate.status) {
          showErrorMessage(addTemplate.message);
        } else {
          showMessage("Template Created successfully");
           navigate(`/note/list`);
          resetForm();
        }
      } catch (error: any) {
        const msg = error?.data?.message || error?.message || "Something went wrong";
        showErrorMessage(msg);
      }
    },
  });

  const formValid = useMemo(() => {
    return (
      formik.values.templateName !== "" &&
      formik.values.templateName !== undefined &&
      formik.values.subject !== "" &&
      formik.values.subject !== undefined &&
      stripHtmlTags(formik.values.message) !=="" &&
      stripHtmlTags(formik.values.message) !==undefined 
    );
  }, [formik.values.templateName, formik.values.subject, formik.values.message]);

  const gridHeight = isMobile ? "30px" : "400px";

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={3} md={3} sm={12} xs={12}>
          <Card className={classes.stepperCard}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ borderBottom: 2, padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                pt={2}
                alignContent={"center"}
              >
                <Typography className={classes.cardTitle}>
                  Create Note Template
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item lg={9} md={9} sm={12} xs={12}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Template Information
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Template Name
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Enter Template Name"
                            size="small"
                            {...formik.getFieldProps("templateName")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            error={formik.touched.templateName && Boolean(formik.errors.templateName)}
                         helperText={formik.touched.templateName && formik.errors.templateName}
                          />
                        </Grid>
                      </Grid>
                    </Grid>


                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Subject
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Enter Subject"
                            size="small"
                            {...formik.getFieldProps("subject")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          error={formik.touched.subject && Boolean(formik.errors.subject)}
                         helperText={formik.touched.subject && formik.errors.subject}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Message
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                        <div
        style={{
          border: formik.touched.message && formik.errors.message
            ? "1px solid #d32f2f"
            : "1px solid #ccc",
          borderRadius: "4px",
          minHeight: "210px",
          overflow: "hidden",
        }}
    >
                <ReactQuill
                  value={formik.values.message}
                  onChange={(value) => formik.setFieldValue("message", value)}
                  onBlur={() => formik.setFieldTouched("message", true)} // Mark as touched
                  style={{
                    height: "210px",
                  }}
                  
                />
                  </div>
                  {formik.touched.message && formik.errors.message && (
      <Typography
        variant="caption"
        style={{ color: "#d32f2f", marginTop: "4px", marginLeft:'15px', display: "block" }}
      >
        {formik.errors.message}
      </Typography>
    )} 
  
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                <Card className={classes.card}>
                  <Grid container spacing={3} p={"24px"}>
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        className={classes.addButton}
                        onClick={() => formik.resetForm()}
                      >
                        Cancel
                      </Button>
                      <Box m={0.5}></Box>

                      <Button
                        variant="contained"
                        startIcon={<SaveAltIcon />}
                        className={classes.addButton}
                        onClick={() => formik.handleSubmit()}
                       // disabled={!formValid || isLoading}
                        disabled={isLoading}
                      >
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
